import {
  type ThemeOptions,
  type ComponentsProps,
  type ComponentsOverrides,
  type ComponentsVariants
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import get from 'lodash/get';
import { HeroVariants } from './Hero.types';

// Layout configuration for media columns per variant
export const layoutConfig = {
  [HeroVariants.default]: { xs: 12, md: 6 },
  [HeroVariants.mediaOnRight]: { xs: 12, md: 6 },
  // [HeroVariants.mediaOnRightFullBleed]: { xs: 12, md: 6 },
  [HeroVariants.mediaOnLeft]: { xs: 12, md: 6 }
  // [HeroVariants.mediaOnLeftFullBleed]: { xs: 12, md: 6 },
  // [HeroVariants.mediaAbove]: { xs: 12 },
  // [HeroVariants.mediaBelow]: { xs: 12 },
  // [HeroVariants.imageOnly]: { xs: 12 },
  // [HeroVariants.simple]: { xs: 12, md: 8 },
  // [HeroVariants.mediaSmall]: { xs: 12, md: 4 },
  // [HeroVariants.news]: { xs: 12, md: 6 }
};

const defaultProps: ComponentsProps['Hero'] = {
  variant: HeroVariants.default
};

const styleOverrides: ComponentsOverrides<Theme>['Hero'] = {
  root: ({ theme, ownerState }) => {
    const backgroundColor = ownerState?.backgroundColor;

    if (backgroundColor?.includes('gradient') && get(theme.palette, backgroundColor)) {
      return {
        background: get(theme.palette, backgroundColor)?.main,
        color: get(theme.palette, `${backgroundColor}.contrastText`),
        containerType: 'inline-size',
        position: 'relative',
        minHeight: 'calc(100vh - 196px)',
        display: 'flex',
        overflow: 'hidden'
      };
    }

    const parsedBGColor = backgroundColor?.includes('.') ? backgroundColor : `${backgroundColor}.main`;
    const paletteColor = backgroundColor?.includes('.') ? backgroundColor.split('.')[0] : `${backgroundColor}`;

    if (backgroundColor && get(theme.palette, parsedBGColor)) {
      return {
        backgroundColor: get(theme.palette, parsedBGColor),
        color: get(theme.palette, `${paletteColor}.contrastText`),
        containerType: 'inline-size',
        position: 'relative',
        minHeight: 'calc(100vh - 196px)',
        display: 'flex',
        overflow: 'hidden'
      };
    }

    return {
      containerType: 'inline-size',
      position: 'relative',
      minHeight: 'calc(100vh - 196px)',
      display: 'flex',
      overflow: 'hidden'
    };
  },

  contentOuterGrid: ({ theme }) => ({
    width: '100%',
    [theme.breakpoints.up('md')]: {
      minHeight: '50vh'
    }
  }),

  mainContentWrap: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gridColumnStart: 'start',
    gridColumnEnd: 'end',
    zIndex: 2
  },

  content: ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(4, 0),
    // Add horizontal padding on mobile to prevent text from touching screen edges
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      paddingLeft: 0,
      paddingRight: 0
    }
  }),
  contentInnerWrap: ({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',
    'justifyContent': 'center',
    'gridColumnStart': 'start',
    'gridColumnEnd': 'end',
    'gap': theme.spacing(2),
    '*': {
      margin: 0
    }
  }),
  // title: ({ theme, ownerState }) => ({}),

  // subtitle: ({ theme }) => ({}),

  // overline: ({ theme }) => ({}),

  // body: ({ theme }) => ({}),

  actionsWrap: ({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(3),
    marginTop: theme.spacing(3),
    flexWrap: 'wrap',
    [theme.breakpoints.up('sm')]: {
      flexWrap: 'nowrap'
    }
  }),

  mediaWrap: ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gridColumnStart: 'start',
    gridColumnEnd: 'end'
  }),

  media: ({ ownerState }) => ({
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  }),

  breadcrumbsWrap: ({ theme }) => ({
    gridColumnStart: 'start',
    gridColumnEnd: 'end',
    zIndex: 10,
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      bottom: theme.spacing(3)
    }
  }),

  background: {
    'position': 'absolute',
    'top': 0,
    'left': 0,
    'width': '100%',
    'height': '100%',
    'zIndex': 1,

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center'
    }
  }
};

const createVariants = (theme: Theme): ComponentsVariants['Hero'] => [
  // Old theme variants
  {
    props: { variant: HeroVariants.alignLeft },
    style: {
      '[class*="Hero-contentOuterGrid"]': {
        justifyContent: 'flex-start'
      },
      '[class*="Hero-content"]': {
        alignItems: 'flex-start',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
          paddingLeft: 0,
          paddingRight: 0
        }
      },
      '[class*="Hero-title"], [class*="Hero-subtitle"], [class*="Hero-overline"], [class*="Hero-body"]': {
        textAlign: 'left'
      },
      '[class*=mainContentWrap]': {
        gridRow: 2,
        [theme.breakpoints.up('md')]: {
          gridRow: 1,
          gridColumnEnd: 'seven-start'
        }
      },
      '[class*=mediaWrap]': {
        gridRow: 1,
        [theme.breakpoints.up('md')]: {
          gridColumnStart: 'seven-end'
        }
      }
    }
  },
  {
    props: { variant: HeroVariants.alignCenter },
    style: {
      '[class*="Hero-contentOuterGrid"]': {
        justifyContent: 'center'
      },
      '[class*="Hero-content"]': {
        alignItems: 'center',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1)
      },
      '[class*="Hero-title"], [class*="Hero-subtitle"], [class*="Hero-overline"], [class*="Hero-body"]': {
        textAlign: 'center'
      },
      '[class*="Hero-contentInnerWrap"]': {
        minWidth: '50%'
      }
    }
  },
  {
    props: { contentHeight: 'md' },
    style: {
      'minHeight': 'unset',
      '&[class*="Hero-root"]': {
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(4)
      },
      '[class*="Hero-contentOuterGrid"]': {
        minHeight: 'auto'
      },
      '[class*="Hero-content"]': {
        padding: 0,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        gap: theme.spacing(1.5),
        [theme.breakpoints.up('md')]: {
          paddingLeft: 0,
          paddingRight: 0
        }
      },
      '[class*="Hero-contentInnerWrap"]': {
        gap: theme.spacing(1)
      },
      '.MuiTypography-root.MuiTypography-body1': {
        ...theme.typography.body2,
        '.MuiLink-root': {
          'textDecoration': 'underline',
          'textDecorationColor': 'inherit',
          'color': 'inherit',
          '&:hover': {
            textDecoration: 'none'
          }
        }
      }
    }
  },
  {
    props: { contentHeight: 'sm' },
    style: {
      'minHeight': '0 !important',
      '&[class*="Hero-root"]': {
        minHeight: 0
      },
      '[class*="Hero-contentOuterGrid"]': {
        minHeight: 'auto'
      },
      '[class*="Hero-mainContentWrap"]': {
        paddingLeft: theme.spacing(6)
      },
      '[class*="Hero-content"]': {
        padding: 0,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
          paddingLeft: 0,
          paddingRight: 0
        }
      },
      '[class*="Hero-contentInnerWrap"]': {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        gap: 0
      },
      '[class*="Hero-mediaWrap"]': {
        paddingRight: 0
      },
      '.MuiTypography-h1': {
        marginBottom: theme.spacing(2)
      },
      '.MuiTypography-h5': {
        ...theme.typography.body1,
        marginBottom: theme.spacing(3)
      }
    }
  },
  // Media on Right variants
  {
    props: { variant: HeroVariants.mediaOnRight },
    style: {
      '[class*=mainContentWrap]': {
        gridRow: 2,
        [theme.breakpoints.up('md')]: {
          gridRow: 1,
          gridColumnEnd: 'six-start'
        }
      },
      '[class*=mediaWrap]': {
        gridRow: 1,
        [theme.breakpoints.up('md')]: {
          gridColumnStart: 'six-end'
        }
      }
    }
  },

  // {
  //   props: { variant: HeroVariants.mediaOnRightFullBleed },
  //   style: {
  //     '[class*=mainContentWrap]': {
  //       gridRow: 2,
  //       [theme.breakpoints.up('md')]: {
  //         gridRow: 1,
  //         gridColumnEnd: 'half'
  //       }
  //     },
  //     '[class*=mediaWrap]': {
  //       gridRow: 1,
  //       gridColumnStart: 'full-start',
  //       gridColumnEnd: 'full-end',
  //       [theme.breakpoints.up('md')]: {
  //         gridColumnStart: 'half'
  //       }
  //     }
  //   }
  // },

  // Media on Left variants
  {
    props: { variant: HeroVariants.mediaOnLeft },
    style: {
      '[class*=mainContentWrap]': {
        gridRow: 2,
        [theme.breakpoints.up('md')]: {
          gridRow: 1,
          gridColumnStart: 'half'
        }
      },
      '[class*=mediaWrap]': {
        gridRow: 1,
        [theme.breakpoints.up('md')]: {
          gridColumnEnd: 'half'
        }
      }
    }
  }

  // {
  //   props: { variant: HeroVariants.mediaOnLeftFullBleed },
  //   style: {
  //     '[class*=mainContentWrap]': {
  //       gridRow: 2,
  //       [theme.breakpoints.up('md')]: {
  //         gridRow: 1,
  //         gridColumnStart: 'half'
  //       }
  //     },
  //     '[class*=mediaWrap]': {
  //       gridRow: 1,
  //       gridColumnStart: 'full-start',
  //       gridColumnEnd: 'full-end',
  //       [theme.breakpoints.up('md')]: {
  //         gridColumnEnd: 'half'
  //       }
  //     }
  //   }
  // },

  // Stacked variants
  // {
  //   props: { variant: HeroVariants.mediaAbove },
  //   style: {
  //     '[class*=mainContentWrap]': {
  //       gridRow: 2
  //     },
  //     '[class*=mediaWrap]': {
  //       gridRow: 1
  //     }
  //   }
  // },

  // {
  //   props: { variant: HeroVariants.mediaBelow },
  //   style: {
  //     '[class*=mainContentWrap]': {
  //       gridRow: 1
  //     },
  //     '[class*=mediaWrap]': {
  //       gridRow: 2
  //     }
  //   }
  // },

  // // Special variants
  // {
  //   props: { variant: HeroVariants.imageOnly },
  //   style: {
  //     'minHeight': 'auto',
  //     'textAlign': 'center',

  //     '[class*=contentOuterGrid]': {
  //       display: 'flex',
  //       justifyContent: 'center',
  //       padding: theme.spacing(6)
  //     },

  //     '[class*=title]': {
  //       ...theme.typography.h1
  //     },

  //     '[class*=subtitle]': {
  //       ...theme.typography.h2,
  //       color: theme.vars.palette.primary.main
  //     }
  //   }
  // },

  // {
  //   props: { variant: HeroVariants.simple },
  //   style: {
  //     '[class*=mainContentWrap]': {
  //       [theme.breakpoints.up('md')]: {
  //         gridColumnEnd: 'three-quarter'
  //       }
  //     }
  //   }
  // },

  // {
  //   props: { variant: HeroVariants.mediaSmall },
  //   style: {
  //     '[class*=mainContentWrap]': {
  //       gridRow: 2,
  //       [theme.breakpoints.up('md')]: {
  //         gridRow: 1,
  //         gridColumnEnd: 'three-quarter'
  //       }
  //     },
  //     '[class*=mediaWrap]': {
  //       gridRow: 1,
  //       minHeight: '30vh',
  //       [theme.breakpoints.up('md')]: {
  //         gridColumnStart: 'three-quarter',
  //         alignItems: 'flex-end'
  //       }
  //     }
  //   }
  // }
];

export const heroTheme = (theme: Theme): ThemeOptions => ({
  components: {
    Hero: {
      defaultProps,
      // @ts-expect-error - MUI styled component type incompatibility with React 19
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default heroTheme;
